import React, { useState } from "react";
import { Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { mockPosts, allUsers, featuredCreators } from "../data/mockData";

const GlobalSearch = ({ onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({ creators: [], posts: [], users: [] });
  const navigate = useNavigate();

  const handleSearch = (searchQuery) => {
    setQuery(searchQuery);
    
    if (!searchQuery.trim()) {
      setResults({ creators: [], posts: [], users: [] });
      return;
    }

    const lowerQuery = searchQuery.toLowerCase();

    const creators = featuredCreators.filter(creator =>
      creator.username.toLowerCase().includes(lowerQuery) ||
      creator.displayName.toLowerCase().includes(lowerQuery) ||
      creator.category.toLowerCase().includes(lowerQuery)
    );

    const posts = mockPosts.filter(post =>
      post.title?.toLowerCase().includes(lowerQuery) ||
      post.content?.toLowerCase().includes(lowerQuery)
    );

    const users = allUsers.filter(user =>
      user.name.toLowerCase().includes(lowerQuery) ||
      user.email.toLowerCase().includes(lowerQuery)
    ).slice(0, 5);

    setResults({ creators, posts, users });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/fan/search?q=${encodeURIComponent(query)}`);
      onClose();
    }
  };

  const hasResults = results.creators.length > 0 || results.posts.length > 0 || results.users.length  > 0;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-0 sm:p-4 sm:pt-20"
      onClick={onClose}
    >
      <div 
        className="bg-white sm:rounded-lg shadow-2xl w-full h-full sm:h-auto sm:max-w-2xl sm:max-h-[75vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input */}
        <form onSubmit={handleSubmit} className="p-3 sm:p-4 border-b border-slate-200 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search creators, content..."
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              autoFocus
            />
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X size={18} />
            </button>
          </div>
        </form>

        {/* Results */}
        <div className="overflow-y-auto flex-1">
          {!query && (
            <div className="p-8 text-center text-slate-500">
              <Search size={40} className="mx-auto mb-3 text-slate-300" />
              <p className="text-sm">Start typing to search...</p>
            </div>
          )}

          {query && !hasResults && (
            <div className="p-8 text-center text-slate-500">
              <p className="text-sm">No results for "{query}"</p>
            </div>
          )}

          {/* Creators */}
          {results.creators.length > 0 && (
            <div className="p-3 sm:p-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-xs sm:text-sm font-semibold text-slate-600">Creators</h3>
                <button 
                  onClick={() => {
                    navigate(`/fan/search?q=${encodeURIComponent(query)}`);
                    onClose();
                  }}
                  className="text-xs text-primary font-medium hover:underline"
                >
                  See all
                </button>
              </div>
              <div className="space-y-1">
                {results.creators.slice(0, 3).map((creator) => (
                  <div
                    key={creator.id}
                    onClick={() => {
                      navigate(`/fan/creator/${creator.username}`);
                      onClose();
                    }}
                    className="flex items-center p-2 sm:p-3 hover:bg-slate-50 rounded-lg cursor-pointer transition"
                  >
                    <img
                      src={creator.avatarUrl || `https://i.pravatar.cc/48?u=${creator.id}`}
                      alt={creator.displayName}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full mr-3 flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 text-sm sm:text-base truncate">{creator.displayName}</p>
                      <p className="text-xs sm:text-sm text-slate-500 truncate">@{creator.username} • {creator.category}</p>
                    </div>
                    <span className="text-xs text-slate-400 flex-shrink-0 ml-2">{creator.followers} followers</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Posts */}
          {results.posts.length > 0 && (
            <div className="p-3 sm:p-4 border-t border-slate-200">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-xs sm:text-sm font-semibold text-slate-600">Posts</h3>
                <button 
                  onClick={() => {
                    navigate(`/fan/search?q=${encodeURIComponent(query)}`);
                    onClose();
                  }}
                  className="text-xs text-primary font-medium hover:underline"
                >
                  See all
                </button>
              </div>
              <div className="space-y-1">
                {results.posts.slice(0, 3).map((post) => (
                  <div
                    key={post.id}
                    onClick={() => {
                      navigate(`/fan/search?q=${encodeURIComponent(query)}`); // Ideally navigate to post detail
                      onClose();
                    }}
                    className="p-2 sm:p-3 hover:bg-slate-50 rounded-lg cursor-pointer transition"
                  >
                    <p className="font-medium text-slate-800 line-clamp-1 text-sm sm:text-base">{post.title || "Untitled"}</p>
                    <p className="text-xs sm:text-sm text-slate-500 line-clamp-1">{post.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {hasResults && (
             <div className="p-4 border-t border-slate-100 text-center">
               <button 
                 onClick={() => {
                    navigate(`/fan/search?q=${encodeURIComponent(query)}`);
                    onClose();
                 }}
                 className="text-primary font-semibold text-sm hover:underline"
               >
                 View all results for "{query}"
               </button>
             </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;
